/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 20/04/22
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useGetRequest } from '@/utils/useStatefulRequests';
import { ListResponse } from '@/wpt-lib/interfaces/listResponse';
import { User } from '@/wpt-lib/interfaces/User';
import { debounce } from 'lodash';
import { Box, Button, Flex, Input, InputGroup, InputRightElement, Spinner, Text, VStack } from '@chakra-ui/react';
import { useAsyncLayoutEffect } from '@/utils/hooks/useAsyncEffect';
import StateDisplay from '@/components/StateDisplay/StateDisplay';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import { formatName } from '@/wpt-lib/utils/formatName';

interface UserSearchProps {
    disabled?: boolean;
    onSelect(user: User): void;
}

const UserSearch = (props: UserSearchProps) => {
    const focusRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [searchTerms, setSearchTerms] = useState<string>('');
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        const func = (ev: FocusEvent) => {
            if (!ev.target || !focusRef.current?.contains(ev.target as Node)) {
                setIsOpen(false);
            }
        };
        window.addEventListener('focusin', func);
        return () => window.removeEventListener('focusin', func);
    }, []);

    const { makeRequest: getUsers, responseData: users, loading, error } = useGetRequest<ListResponse<User>>();

    const doSearch = useCallback(
        debounce(async (query: string) => {
            await getUsers(`users`, {
                params: {
                    pageSize: 5,
                    query: query,
                },
            });
            setIsOpen(true);
        }, 1000),
        [],
    );

    useAsyncLayoutEffect(async () => {
        if (searchTerms) {
            doSearch(searchTerms);
        } else {
            doSearch.cancel();
            setIsOpen(false);
        }
    }, [searchTerms]);

    const handleSelectUser = (user: User) => {
        setIsOpen(false);
        setSearchTerms('');
        props.onSelect(user);
    };

    const handleFocus = () => {
        if (searchTerms) {
            setIsOpen(true);
        }
    };

    return (
        <Box
            w="full"
            ref={focusRef}
            onKeyDown={(ev) => {
                if (ev.key === 'Escape') {
                    if (inputRef.current) {
                        inputRef.current.focus();
                    }
                    setIsOpen(false);
                    ev.stopPropagation();
                }
            }}
        >
            <InputGroup>
                <Input
                    onChange={(ev) => setSearchTerms(ev.target.value)}
                    value={searchTerms}
                    isDisabled={props.disabled}
                    w="full"
                    onFocus={handleFocus}
                    ref={inputRef}
                />
                <InputRightElement>{loading && <Spinner size="sm" />}</InputRightElement>
            </InputGroup>
            {isOpen && !loading && users && (
                <Flex position={'relative'} w={'full'}>
                    <VStack
                        position={'absolute'}
                        spacing={2}
                        p={2}
                        mt={2}
                        w={'full'}
                        background={'offWhite'}
                        borderWidth={1}
                        borderColor="gray.200"
                        borderStyle="solid"
                        shadow={'lg'}
                        rounded={'lg'}
                        zIndex={2}
                        maxHeight={300}
                        overflowY="auto"
                        tabIndex={-1}
                    >
                        <StateDisplay
                            data={users}
                            loading={loading}
                            error={!!error}
                            errorRender={() => <SimpleAlert variant="subtle">Could not load users</SimpleAlert>}
                            render={(data) => {
                                if (data.items.length === 0) {
                                    return <Text p={3}>No users found</Text>;
                                }
                                return (
                                    <>
                                        {data.items.map((user, i) => (
                                            <VStack
                                                key={i}
                                                as={Button}
                                                w={'full'}
                                                h={'full'}
                                                alignItems={'flex-start'}
                                                spacing={0}
                                                fontWeight="normal"
                                                _focus={{
                                                    background: 'gray.200',
                                                }}
                                                p={3}
                                                onClick={() => handleSelectUser(user)}
                                            >
                                                <Text fontWeight="bold">{formatName(user)}</Text>
                                                <Text>{user.staffId}</Text>
                                                <Text>{user.email}</Text>
                                            </VStack>
                                        ))}
                                    </>
                                );
                            }}
                        />
                    </VStack>
                </Flex>
            )}
        </Box>
    );
};

export default UserSearch;
